import { Certification } from '@applicator/shared'
import React, { useState, useCallback, useEffect, useRef } from 'react'

import CustomCheckbox from '../CustomCheckbox'
import {
  cascadeCertificationIncluded,
  defaultNewCertification,
  isCertificationNestedFieldIncluded,
  toggleCertificationField,
  type CertificationFieldFlag,
} from '../../utils/certificationInclusion'
import { reorderItemsById } from './useWorkExperienceRoleHandlers/reorderHelpers'
import WorkExperienceDragHandle from './WorkExperienceDragHandle'
import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'
import {
  certDatesEditorClass,
  certIssuerEditorClass,
  certNameEditorClass,
  certUrlEditorClass,
} from '../../utils/certificationEditorTypography'
import CertificationsPortals from './CertificationsPortals'
import { useDatePickerAnchorPosition } from './useDatePickerAnchorPosition'
import { createDatePickerPosition } from './useWorkExperienceRoleHandlers/datePickerHelpers'

interface CertificationsEditorProps {
  data: Certification[]
  onChange: (data: Certification[]) => void
  onAddCertificationRef?: (fn: () => void) => void
}

export default function CertificationsEditor({ data, onChange, onAddCertificationRef }: CertificationsEditorProps) {
  const certifications = Array.isArray(data) ? data : []
  const [editingNameId, setEditingNameId] = useState<string | null>(null)
  const [editingIssuerId, setEditingIssuerId] = useState<string | null>(null)
  const [editingCredentialUrlId, setEditingCredentialUrlId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingIssuer, setEditingIssuer] = useState('')
  const [editingStartDate, setEditingStartDate] = useState('')
  const [editingEndDate, setEditingEndDate] = useState('')
  const [editingCredentialUrl, setEditingCredentialUrl] = useState('')
  const [datePickerState, setDatePickerState] = useState<{
    certId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null>(null)
  const dateFieldRefs = useRef<Record<string, HTMLElement | null>>({})
  const datePickerRef = useRef<HTMLDivElement>(null)
  const [hoveredCertId, setHoveredCertId] = useState<string | null>(null)
  const [draggedCertId, setDraggedCertId] = useState<string | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const clearCertDrag = () => {
    setDraggedCertId(null)
    setDragOverIndex(null)
  }

  const handleReorderCertifications = (draggedId: string, targetIndex: number) => {
    onChange(reorderItemsById(certifications, draggedId, targetIndex))
  }

  const handleAddCertification = useCallback(() => {
    const newCertification = defaultNewCertification(`certification-${Date.now()}`)
    onChange([...certifications, newCertification])
    setTimeout(() => {
      setEditingNameId(newCertification.id)
      setEditingName('')
    }, 0)
  }, [certifications, onChange])

  useEffect(() => {
    if (onAddCertificationRef) {
      onAddCertificationRef(handleAddCertification)
    }
    return () => {
      if (onAddCertificationRef) {
        onAddCertificationRef(() => {})
      }
    }
  }, [handleAddCertification, onAddCertificationRef])

  const handleDeleteCertification = (certId: string) => {
    onChange(certifications.filter((cert) => cert.id !== certId))
  }

  const handleUpdateCertification = (
    certId: string,
    field: 'name' | 'issuer' | 'startDate' | 'endDate' | 'credentialUrl',
    value: string
  ) => {
    onChange(
      certifications.map((cert) => (cert.id === certId ? { ...cert, [field]: value } : cert))
    )
  }

  const handleToggleCertification = (certId: string) => {
    onChange(
      certifications.map((cert) =>
        cert.id === certId ? cascadeCertificationIncluded(cert, !cert.included) : cert
      )
    )
  }

  const handleToggleField = (certId: string, field: CertificationFieldFlag) => {
    const target = certifications.find((c) => c.id === certId)
    if (!target?.included) return
    onChange(
      certifications.map((cert) =>
        cert.id === certId ? toggleCertificationField(cert, field) : cert
      )
    )
  }

  const renderCheckbox = (checked: boolean, onToggle: () => void) => (
    <CustomCheckbox checked={checked} onChange={onToggle} size="sm" />
  )

  const openDatePicker = (
    cert: Certification,
    field: 'startDate' | 'endDate',
    event: React.MouseEvent,
  ) => {
    event.stopPropagation()
    const triggerElement = event.currentTarget as HTMLElement
    const triggerKey = `${cert.id}-${field}`
    dateFieldRefs.current[triggerKey] = triggerElement
    setDatePickerState({
      certId: cert.id,
      field,
      position: createDatePickerPosition(triggerElement, { monthYearOnly: true }),
      triggerKey,
    })
    if (field === 'startDate') {
      setEditingStartDate(cert.startDate || '')
    } else {
      setEditingEndDate(cert.endDate || '')
    }
  }

  const handleDateChange = (value: string) => {
    if (!datePickerState) return
    handleUpdateCertification(datePickerState.certId, datePickerState.field, value)
    if (datePickerState.field === 'startDate') {
      setEditingStartDate(value)
    } else {
      setEditingEndDate(value)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerState && datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement
        const triggerElement = dateFieldRefs.current[datePickerState.triggerKey]
        if (triggerElement && !triggerElement.contains(target)) {
          setDatePickerState(null)
        }
      }
    }

    if (datePickerState) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [datePickerState])

  useDatePickerAnchorPosition(
    datePickerState,
    dateFieldRefs,
    setDatePickerState,
    datePickerRef,
    { monthYearOnly: true },
  )

  return (
    <div className="space-y-4">
      {certifications.map((cert, certIndex) => {
        const isDragging = draggedCertId === cert.id
        const isDragOver = dragOverIndex === certIndex && draggedCertId !== cert.id

        return (
        <div
          key={cert.id}
          data-certification-id={cert.id}
          draggable
          onDragStart={(e) => {
            setDraggedCertId(cert.id)
            e.dataTransfer.effectAllowed = 'move'
            e.dataTransfer.setData('text/plain', '')
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            if (draggedCertId && draggedCertId !== cert.id) {
              setDragOverIndex(certIndex)
            }
          }}
          onDragLeave={() => {
            if (dragOverIndex === certIndex) {
              setDragOverIndex(null)
            }
          }}
          onDrop={(e) => {
            e.preventDefault()
            if (draggedCertId && draggedCertId !== cert.id) {
              handleReorderCertifications(draggedCertId, certIndex)
            }
            clearCertDrag()
          }}
          onDragEnd={clearCertDrag}
          className={`group/cert flex items-start gap-2 cursor-move transition-all ${
            isDragging ? 'opacity-50' : ''
          } ${isDragOver ? 'ring-2 ring-interactive-focus-ring ring-inset rounded-lg' : ''}`}
          onMouseEnter={() => setHoveredCertId(cert.id)}
          onMouseLeave={() => setHoveredCertId(null)}
        >
          <WorkExperienceDragHandle
            group="cert"
            title="Перетащите сертификат для изменения порядка"
          />
          <div className="flex-1 min-w-0 space-y-2 relative border border-border-default rounded-lg p-3 bg-surface-default">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 flex-1 rounded transition-colors">
              {renderCheckbox(cert.included, () => handleToggleCertification(cert.id))}
              <div className="flex-1">
                {editingNameId === cert.id ? (
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => {
                      handleUpdateCertification(cert.id, 'name', editingName)
                      setEditingNameId(null)
                      setEditingName('')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleUpdateCertification(cert.id, 'name', editingName)
                        setEditingNameId(null)
                        setEditingName('')
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        setEditingNameId(null)
                        setEditingName('')
                      }
                    }}
                    placeholder="Certification name"
                    className="w-full px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                    autoFocus
                  />
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setEditingNameId(cert.id)
                      setEditingName(cert.name || '')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setEditingNameId(cert.id)
                        setEditingName(cert.name || '')
                      }
                    }}
                    className="cursor-text rounded p-1 -m-1"
                  >
                    <div className={`text-sm ${certNameEditorClass(cert)}`}>
                      {cert.name || <span className="italic">Certification name</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteCertification(cert.id)
              }}
              className={`p-1.5 text-text-muted hover:text-danger transition-opacity ${
                hoveredCertId === cert.id ? 'opacity-100' : 'opacity-0'
              }`}
              title="Delete certification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          <div
            className={`flex items-center gap-3 ml-6 ${cert.included ? '' : 'opacity-50 pointer-events-none'}`}
          >
            {renderCheckbox(isCertificationNestedFieldIncluded(cert, 'issuerIncluded'), () =>
              handleToggleField(cert.id, 'issuerIncluded')
            )}
            <div className="flex-1">
              {editingIssuerId === cert.id ? (
                <input
                  type="text"
                  value={editingIssuer}
                  onChange={(e) => setEditingIssuer(e.target.value)}
                  onBlur={() => {
                    handleUpdateCertification(cert.id, 'issuer', editingIssuer)
                    setEditingIssuerId(null)
                    setEditingIssuer('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleUpdateCertification(cert.id, 'issuer', editingIssuer)
                      setEditingIssuerId(null)
                      setEditingIssuer('')
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setEditingIssuerId(null)
                      setEditingIssuer('')
                    }
                  }}
                  placeholder="Issuing organization"
                  className="w-full px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                  autoFocus
                />
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setEditingIssuerId(cert.id)
                    setEditingIssuer(cert.issuer || '')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditingIssuerId(cert.id)
                      setEditingIssuer(cert.issuer || '')
                    }
                  }}
                  className="cursor-text rounded p-1 -m-1"
                >
                  <div className={`text-xs ${certIssuerEditorClass(cert)}`}>
                    {cert.issuer || <span className="italic">Issuing organization</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            className={`flex items-center gap-3 ml-6 ${cert.included ? '' : 'opacity-50 pointer-events-none'}`}
          >
            {renderCheckbox(isCertificationNestedFieldIncluded(cert, 'datesIncluded'), () =>
              handleToggleField(cert.id, 'datesIncluded')
            )}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <div
                  ref={(el) => {
                    dateFieldRefs.current[`${cert.id}-startDate`] = el
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => openDatePicker(cert, 'startDate', e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openDatePicker(cert, 'startDate', e as unknown as React.MouseEvent)
                    }
                  }}
                  className="cursor-pointer hover:bg-surface-muted rounded px-1 -mx-1"
                >
                  <div className={`text-xs ${certDatesEditorClass(cert)}`}>
                    {cert.startDate?.trim() || (
                      <span className="italic">Start (MM/YYYY)</span>
                    )}
                  </div>
                </div>
                <span className="text-xs text-text-muted">–</span>
                <div
                  ref={(el) => {
                    dateFieldRefs.current[`${cert.id}-endDate`] = el
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => openDatePicker(cert, 'endDate', e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openDatePicker(cert, 'endDate', e as unknown as React.MouseEvent)
                    }
                  }}
                  className="cursor-pointer hover:bg-surface-muted rounded px-1 -mx-1"
                >
                  <div className={`text-xs ${certDatesEditorClass(cert)}`}>
                    {cert.endDate?.trim() || (
                      <span className="italic">End (MM/YYYY)</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`flex items-center gap-3 ml-6 ${cert.included ? '' : 'opacity-50 pointer-events-none'}`}
          >
            {renderCheckbox(isCertificationNestedFieldIncluded(cert, 'credentialUrlIncluded'), () =>
              handleToggleField(cert.id, 'credentialUrlIncluded')
            )}
            <div className="flex-1">
              {editingCredentialUrlId === cert.id ? (
                <input
                  type="url"
                  value={editingCredentialUrl}
                  onChange={(e) => setEditingCredentialUrl(e.target.value)}
                  onBlur={() => {
                    handleUpdateCertification(cert.id, 'credentialUrl', editingCredentialUrl)
                    setEditingCredentialUrlId(null)
                    setEditingCredentialUrl('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleUpdateCertification(cert.id, 'credentialUrl', editingCredentialUrl)
                      setEditingCredentialUrlId(null)
                      setEditingCredentialUrl('')
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setEditingCredentialUrlId(null)
                      setEditingCredentialUrl('')
                    }
                  }}
                  placeholder="Credential URL"
                  className="w-full px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                  autoFocus
                />
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setEditingCredentialUrlId(cert.id)
                    setEditingCredentialUrl(cert.credentialUrl || '')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditingCredentialUrlId(cert.id)
                      setEditingCredentialUrl(cert.credentialUrl || '')
                    }
                  }}
                  className="cursor-text rounded p-1 -m-1"
                >
                  {cert.credentialUrl ? (
                    <a
                      href={cert.credentialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={`text-xs underline ${certUrlEditorClass(cert)} hover:text-interactive`}
                    >
                      {cert.credentialUrl}
                    </a>
                  ) : (
                    <div className={`text-xs italic ${certUrlEditorClass(cert)}`}>Credential URL</div>
                  )}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
        )
      })}

      {certifications.length === 0 && (
        <ResumeSectionEmptyHint sectionType="certifications" />
      )}

      <button
        type="button"
        onClick={handleAddCertification}
        className="text-sm text-interactive hover:text-interactive font-medium"
      >
        + Add Certification
      </button>

      <CertificationsPortals
        datePickerState={datePickerState}
        datePickerRef={datePickerRef}
        editingStartDate={editingStartDate}
        editingEndDate={editingEndDate}
        onDateChange={handleDateChange}
        onClose={() => setDatePickerState(null)}
      />
    </div>
  )
}
